import { FontIcon, mergeStyleSets, useTheme } from '@fluentui/react';
import { IPTooltipHost } from 'baseComponents';
import { getTypeOptions } from 'components/KPI/KPIHelpers';
import { TFunction } from 'i18next';
import { KPITypes } from 'models/kpi/kpi';
import { KeyboardEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const TYPE_ICON: Partial<Record<KPITypes, string>> = {
  [KPITypes.Number]: 'NumberSymbol',
  [KPITypes.SuccesError]: 'SkypeCircleCheck',
  [KPITypes.Choice]: 'BulletedList',
  [KPITypes.Date]: 'Calendar',
  [KPITypes.Text]: 'TextField',
  [KPITypes.TextMultiline]: 'AlignLeft',
  [KPITypes.Html]: 'FileHTML',
  [KPITypes.Expression]: 'Calculator',
};

export interface KPITypePickerProps {
  selectedType: KPITypes;
  restrictTypeChanges: boolean;
  compactLayout?: boolean;
  onPickType: (type: KPITypes) => void;
  onIsFieldChange?: (isField: boolean) => void;
}

const KPITypePicker = (props: KPITypePickerProps) => {
  const { t } = useTranslation(['kpis']);
  const theme = useTheme();
  const typeChipRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const typeOptions = getTypeOptions(props.selectedType, t as TFunction<string[]>, props.restrictTypeChanges);

  const chipStyles = mergeStyleSets({
    chips: { display: 'flex', flexWrap: 'wrap', gap: props.compactLayout ? 6 : 8 },
    chip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: props.compactLayout ? '4px 10px' : '6px 12px',
      borderRadius: 16,
      cursor: 'pointer',
      fontSize: 14,
      border: `1px solid ${theme.semanticColors.inputBorder}`,
      background: theme.semanticColors.bodyBackground,
      color: theme.palette.neutralPrimary,
      selectors: {
        ':hover': { background: theme.palette.neutralLighter },
        ':disabled': { cursor: 'not-allowed', opacity: 0.5, pointerEvents: 'none' },
      },
    },
    chipSelected: {
      border: `1px solid ${theme.palette.themePrimary}`,
      background: theme.palette.themePrimary,
      color: theme.palette.white,
      fontWeight: 600,
      selectors: { ':hover': { background: theme.palette.themeDarkAlt } },
    },
    chipIcon: { fontSize: 16 },
  });

  const pickType = (type: KPITypes) => {
    props.onPickType(type);
    if (type === KPITypes.Expression) {
      props.onIsFieldChange?.(false);
    }
  };

  const onTypeChipKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const direction =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
        ? -1
        : 0;
    if (!direction) return;

    event.preventDefault();
    let next = index;
    do {
      next = (next + direction + typeOptions.length) % typeOptions.length;
    } while (typeOptions[next].disabled && next !== index);

    typeChipRefs.current[next]?.focus();
    if (!typeOptions[next].disabled) pickType(typeOptions[next].key as KPITypes);
  };

  const disabledReason = t('kpis:Fields.TypeLocked');

  return (
    <div className={chipStyles.chips} role="radiogroup" aria-label={t('kpis:Fields.Type')}>
      {typeOptions.map((opt, index) => {
        const selected = props.selectedType === opt.key;

        const chip = (
          <button
            key={opt.key}
            ref={(el) => {
              typeChipRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            disabled={opt.disabled}
            className={selected ? `${chipStyles.chip} ${chipStyles.chipSelected}` : chipStyles.chip}
            onClick={() => pickType(opt.key as KPITypes)}
            onKeyDown={(event) => onTypeChipKeyDown(event, index)}
          >
            <FontIcon iconName={TYPE_ICON[opt.key as KPITypes] ?? 'Tag'} className={chipStyles.chipIcon} />
            {opt.text}
          </button>
        );

        // On edit, non-convertible types render disabled. Explain why on hover and suggest what to do.
        // The disabled button has pointer-events:none (chip styles) so the tooltip host still gets hover.
        return opt.disabled ? (
          <IPTooltipHost key={opt.key} content={disabledReason}>
            {chip}
          </IPTooltipHost>
        ) : (
          chip
        );
      })}
    </div>
  );
};

export default KPITypePicker;
